import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  FiberNew as FiberNewIcon,
} from '@mui/icons-material';

/**
 * SavedSurveyDialog Component
 *
 * Displays when the system detects previously saved survey data for the current user and store.
 * Allows the user to choose between restoring the saved data or starting a new survey.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onChoice - Callback function when user makes a choice
 */
const SavedSurveyDialog = ({ open, onChoice }) => {
  /**
   * Handles the restore choice
   */
  const handleRestore = () => {
    onChoice('restore');
  };

  /**
   * Handles the start new choice
   */
  const handleStartNew = () => {
    onChoice('start_new');
  };

  return (
    <Dialog
      open={open}
      aria-labelledby='saved-survey-dialog-title'
      aria-describedby='saved-survey-dialog-description'
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}}
      sx={{
        // top: '-75%',
        '& .MuiBackdrop-root': {
          height: '200vh',
        },
      }}
    >
      <DialogTitle id='saved-survey-dialog-title'>
        <Typography
          variant='h6'
          component='span'
          style={{ fontFamily: 'Lato' }}
        >
          Previous Survey Found
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id='saved-survey-dialog-description'>
          <Typography variant='body1' paragraph style={{ fontFamily: 'Lato' }}>
            We found a previously saved survey for this store. You can either
            restore your previous answers and continue where you left off, or
            start a completely new survey.
          </Typography>

          <Typography
            variant='body2'
            color='textSecondary'
            style={{ fontFamily: 'Lato' }}
          >
            <strong>Note:</strong> If you choose to start new, your previous
            answers will be permanently deleted and cannot be recovered.
          </Typography>
        </DialogContentText>

        {/* Choice Options */}
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Restore Option */}
          <Box
            sx={{
              p: 2,
              border: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              backgroundColor: 'primary.50',
            }}
          >
            <Typography variant='subtitle2' color='primary' gutterBottom>
              Restore Previous Survey
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Continue with your previously saved answers and progress.
            </Typography>
          </Box>

          {/* Start New Option */}
          <Box
            sx={{
              p: 2,
              border: 1,
              borderColor: 'warning.main',
              borderRadius: 1,
              backgroundColor: 'warning.50',
            }}
          >
            <Typography variant='subtitle2' color='warning.main' gutterBottom>
              Start New Survey
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Begin a fresh survey and delete previous answers.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleRestore}
          variant='contained'
          color='primary'
          size='large'
          startIcon={<RestoreIcon />}
          sx={{ mr: 1 }}
        >
          Restore Previous
        </Button>

        <Button
          onClick={handleStartNew}
          variant='outlined'
          color='warning'
          size='large'
          startIcon={<FiberNewIcon />}
        >
          Start New
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SavedSurveyDialog;
